import type { ProviderUser } from "@/common/types/ProviderUser";
import { isNative } from "@/common/utils/environment";
import { ProviderOnboardingStep } from "../enums/ProviderOnboardingStep";
import { isAccountDetailsComplete } from "./is-account-details-complete";

/**
 * Which provider onboarding steps apply and when each is complete. These rules
 * are separate from the step components in `getOnboardingSteps.tsx` because the
 * provider layout's route loader checks them on every navigation, and loaders
 * stay in the app's entry chunk: importing the step components there would ship
 * every onboarding form to every visitor.
 */

/** The Welcome step is no longer shown to providers. */
export type ActiveOnboardingStep = Exclude<
  ProviderOnboardingStep,
  ProviderOnboardingStep.WELCOME
>;

export type OnboardingStepRule = {
  id: ActiveOnboardingStep;
  valid: (user: ProviderUser) => boolean;
  mobileOnly?: boolean;
};

const hasCompletedStep =
  (stepId: ProviderOnboardingStep) => (user: ProviderUser) =>
    user.onboardingStepsCompleted.some((step) => step.stepId === stepId);

// Listed in the order providers complete them.
const ONBOARDING_STEP_RULES: OnboardingStepRule[] = [
  {
    id: ProviderOnboardingStep.ACCOUNT_DETAILS,
    valid: isAccountDetailsComplete,
  },
  {
    id: ProviderOnboardingStep.LICENSES,
    valid: hasCompletedStep(ProviderOnboardingStep.LICENSES),
  },
  {
    id: ProviderOnboardingStep.SHIFT_PREFERENCES,
    valid: hasCompletedStep(ProviderOnboardingStep.SHIFT_PREFERENCES),
  },
  {
    id: ProviderOnboardingStep.TEXT_NOTIFICATIONS,
    valid: hasCompletedStep(ProviderOnboardingStep.TEXT_NOTIFICATIONS),
  },
  {
    id: ProviderOnboardingStep.PUSH_NOTIFICATIONS,
    mobileOnly: true,
    valid: hasCompletedStep(ProviderOnboardingStep.PUSH_NOTIFICATIONS),
  },
  {
    id: ProviderOnboardingStep.UPLOAD_DOCUMENTS,
    valid: hasCompletedStep(ProviderOnboardingStep.UPLOAD_DOCUMENTS),
  },
];

// Steps hidden from roster-only (BU-invited) providers, who never see jobs
// and whose credentials are managed by the business unit: the job-preferences
// and credential-upload steps.
const ROSTER_ONLY_HIDDEN_STEPS = new Set<ProviderOnboardingStep>([
  ProviderOnboardingStep.SHIFT_PREFERENCES,
  ProviderOnboardingStep.UPLOAD_DOCUMENTS,
]);

/**
 * Returns the rules for every step visible to this user on the current
 * platform. Filters out mobile-only steps when not on native, and the
 * job-preferences and credential-upload steps for roster-only providers
 * (`jobPostsEnabled === false`).
 */
export function getVisibleOnboardingStepRules(
  user?: ProviderUser | null,
): OnboardingStepRule[] {
  return ONBOARDING_STEP_RULES.filter((step) => {
    if (step.mobileOnly && !isNative()) {
      return false;
    }
    if (
      user?.jobPostsEnabled === false &&
      ROSTER_ONLY_HIDDEN_STEPS.has(step.id)
    ) {
      return false;
    }
    return true;
  });
}

export function hasIncompleteOnboardingSteps(user: ProviderUser): boolean {
  return getVisibleOnboardingStepRules(user).some((step) => !step.valid(user));
}
